import { useEffect, useCallback } from 'react';
import { toast } from 'sonner';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description: string;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({ shortcuts, enabled = true }: UseKeyboardShortcutsOptions) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when user is typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      shortcuts.forEach((shortcut) => {
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const metaMatch = shortcut.meta ? event.metaKey : true;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (ctrlMatch && metaMatch && shiftMatch && altMatch && keyMatch) {
          event.preventDefault();
          shortcut.action();
        }
      });
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
};

// Common keyboard shortcuts for LRDEnE
export const commonShortcuts: KeyboardShortcut[] = [
  {
    key: 'k',
    ctrl: true,
    action: () => {
      // Focus search - will be implemented with search component
      const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      } else {
        toast.info('Search feature coming soon!');
      }
    },
    description: 'Focus search',
  },
  {
    key: 'n',
    ctrl: true,
    action: () => {
      // New case - will trigger new case modal
      const newCaseButton = document.querySelector('[aria-label*="New case"], button:has-text("New Case")') as HTMLButtonElement;
      if (newCaseButton) {
        newCaseButton.click();
      } else {
        toast.info('Use the "New Case" button to create a case');
      }
    },
    description: 'New case',
  },
  {
    key: '/',
    ctrl: true,
    action: () => {
      toast.info('Keyboard Shortcuts:\nCtrl+K: Search\nCtrl+N: New Case\nEsc: Close modals\n? : Show all shortcuts', {
        duration: 5000,
      });
    },
    description: 'Show shortcuts',
  },
  {
    key: 'Escape',
    action: () => {
      // Close modals
      const modals = document.querySelectorAll('[role="dialog"], .modal, [data-modal]');
      modals.forEach((modal) => {
        const closeButton = modal.querySelector('[aria-label="Close"], button:has-text("Close")') as HTMLButtonElement;
        if (closeButton) {
          closeButton.click();
        }
      });
    },
    description: 'Close modals',
  },
];

// Create app-specific shortcuts with handlers
export const createAppShortcuts = (
  handleNewCase?: () => void,
): KeyboardShortcut[] => {
  return [
    {
      key: 'k',
      ctrl: true,
      action: () => {
        // Focus search input
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      },
      description: 'Focus search',
    },
    {
      key: 'n',
      ctrl: true,
      action: () => {
        // Trigger new case creation
        if (handleNewCase) {
          handleNewCase();
        } else {
          const newCaseButton = document.querySelector('[aria-label*="New case"], button:has-text("New Case")') as HTMLButtonElement;
          if (newCaseButton) {
            newCaseButton.click();
          }
        }
      },
      description: 'New case',
    },
    {
      key: '/',
      ctrl: true,
      action: () => {
        toast.info('Keyboard Shortcuts:\nCtrl+K: Search\nCtrl+N: New Case\nEsc: Close modals', {
          duration: 5000,
        });
      },
      description: 'Show shortcuts',
    },
    {
      key: 'Escape',
      action: () => {
        // Close modals
        const modals = document.querySelectorAll('[role="dialog"], .modal, [data-modal]');
        modals.forEach((modal) => {
          const closeButton = modal.querySelector('[aria-label="Close"], button:has-text("Close")') as HTMLButtonElement;
          if (closeButton) {
            closeButton.click();
          }
        });
      },
      description: 'Close modals',
    },
  ];
};

